import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserContextService } from '../../core/auth/user-context.service';
import {
  DashboardSummary,
  DashboardSummaryFilters,
  DashboardSummaryPeriod,
  InMemoryDataService,
  LessonCategorySummary
} from '../../core/data/in-memory-data.service';
import { LocalizationService } from '../../core/i18n/localization.service';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { formatPln } from '../../core/utils/currency.util';
import { PL_DASH_DATE_FORMATS, PlDateAdapter } from '../../core/utils/pl-date-adapter';

interface DashboardCard {
  key: 'resources' | 'instructors' | 'courseInstances' | 'students' | 'lessons' | 'payments' | 'overduePayments';
  icon: string;
  title: string;
  value: string;
  caption: string;
  route: string;
  action: string;
  tooltip?: string;
}

@Component({
  selector: 'app-dashboard',
  imports: [
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatTooltipModule,
    TranslatePipe
  ],
  providers: [
    { provide: DateAdapter, useClass: PlDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: PL_DASH_DATE_FORMATS }
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent {
  private readonly dataService = inject(InMemoryDataService);
  private readonly userContext = inject(UserContextService);
  private readonly localization = inject(LocalizationService);
  private summaryRequestId = 0;

  private readonly currentOrganisationId = this.userContext.currentOrganisationId;
  readonly periodOptions: Array<{ value: DashboardSummaryPeriod; labelKey: string }> = [
    { value: 'thisMonth', labelKey: 'dashboard.filter.thisMonth' },
    { value: 'lastMonth', labelKey: 'dashboard.filter.lastMonth' },
    { value: 'thisYear', labelKey: 'dashboard.filter.thisYear' },
    { value: 'lastYear', labelKey: 'dashboard.filter.lastYear' }
  ];
  readonly selectedPeriod = signal<DashboardSummaryPeriod | null>('thisMonth');
  readonly dateFrom = signal('');
  readonly dateTo = signal('');
  readonly dateFromValue = computed(() => this.parseIsoDate(this.dateFrom()));
  readonly dateToValue = computed(() => this.parseIsoDate(this.dateTo()));
  readonly filteredSummary = signal<DashboardSummary | null>(null);
  readonly loadingSummary = signal(false);
  readonly filterError = signal('');

  readonly organisationName = computed(() => {
    const organisationId = this.currentOrganisationId();
    const organisation = this.dataService.organisations().find((item) => this.sameId(item.id, organisationId));
    return organisation ? (organisation.shortName || organisation.name) : 'OSK';
  });

  readonly summary = computed(() => this.filteredSummary() ?? this.dataService.dashboardSummary() ?? this.fallbackSummary());
  readonly hasActiveFilter = computed(() => this.selectedPeriod() != null || this.dateFrom().length > 0 || this.dateTo().length > 0);
  readonly filterLabel = computed(() => {
    const period = this.selectedPeriod();
    if (this.dateFrom() || this.dateTo()) {
      return this.localization.translate('dashboard.filter.customRange');
    }
    return period ? this.localization.translate(`dashboard.filter.${period}`) : this.localization.translate('dashboard.filter.all');
  });
  readonly plannedLessons = computed(() => this.asNumber(this.summary().lessons.planned));
  readonly completedLessons = computed(() => this.asNumber(this.summary().lessons.completed));
  readonly lessonHours = computed(() => this.asNumber(this.summary().lessons.durationHours));
  readonly lessonCategories = computed(() => this.summary().lessons.byCategory ?? []);
  readonly lessonStatuses = computed(() => [
    {
      key: 'planned',
      count: this.plannedLessons(),
      hours: this.asNumber(this.summary().lessons.plannedDurationHours)
    },
    {
      key: 'completed',
      count: this.completedLessons(),
      hours: this.asNumber(this.summary().lessons.completedDurationHours)
    },
    {
      key: 'cancelled',
      count: this.asNumber(this.summary().lessons.cancelled),
      hours: this.asNumber(this.summary().lessons.cancelledDurationHours)
    }
  ]);
  readonly activeStudents = computed(() => this.asNumber(this.summary().students.active));
  readonly activeInstructors = computed(() => this.asNumber(this.summary().instructors.active));
  readonly availableResources = computed(() => this.asNumber(this.summary().resources.available));
  readonly activeCourses = computed(() => this.asNumber(this.summary().courseInstances.active));
  readonly completedLessonsProgress = computed(() => {
    const total = this.lessonHours() - this.asNumber(this.summary().lessons.cancelledDurationHours);
    return total ? Math.round((this.asNumber(this.summary().lessons.completedDurationHours) / total) * 100) : 0;
  });
  readonly paidAmount = computed(() => this.asNumber(this.summary().payments.paidAmount));
  readonly pendingAmount = computed(() => this.asNumber(this.summary().payments.pendingSettlementAmount));
  readonly overdueAmount = computed(() => Math.abs(Math.min(0, this.asNumber(this.summary().payments.overdueAmount))));

  readonly cards = computed<DashboardCard[]>(() => [
    {
      key: 'resources',
      icon: 'directions_car',
      title: this.localization.translate('entity.resources'),
      value: `${this.availableResources()} / ${this.summary().resources.total}`,
      caption: this.localization.translate('dashboard.resourcesCaption'),
      route: '/resources/resources',
      action: this.localization.translate('dashboard.goToResources')
    },
    {
      key: 'instructors',
      icon: 'person',
      title: this.localization.translate('entity.instructors'),
      value: `${this.activeInstructors()} / ${this.summary().instructors.total}`,
      caption: this.localization.translate('dashboard.instructorsCaption'),
      route: '/resources/instructors',
      action: this.localization.translate('dashboard.goToInstructors')
    },
    {
      key: 'courseInstances',
      icon: 'event',
      title: this.localization.translate('entity.courseInstances'),
      value: `${this.activeCourses()} / ${this.summary().courseInstances.total}`,
      caption: this.localization.translate('dashboard.coursesCaption'),
      route: '/resources/courseInstances',
      action: this.localization.translate('dashboard.goToCourses')
    },
    {
      key: 'students',
      icon: 'school',
      title: this.localization.translate('entity.students'),
      value: `${this.activeStudents()} / ${this.summary().students.total}`,
      caption: this.localization.translate('dashboard.studentsCaption'),
      route: '/resources/students',
      action: this.localization.translate('dashboard.goToStudents')
    },
    {
      key: 'lessons',
      icon: 'schedule',
      title: this.localization.translate('entity.lessons'),
      value: this.localization.translate('dashboard.lessonHoursValue', { hours: this.formatHours(this.lessonHours()) }),
      caption: this.localization.translate('dashboard.lessonHoursCaption', { count: this.summary().lessons.total }),
      route: '/resources/lessons',
      action: this.localization.translate('dashboard.goToLessons')
    },
    {
      key: 'payments',
      icon: 'payments',
      title: this.localization.translate('entity.payments'),
      value: formatPln(this.paidAmount()),
      caption: this.localization.translate('dashboard.unsettledCaption', { amount: formatPln(this.pendingAmount()) }),
      route: '/resources/payments',
      action: this.localization.translate('dashboard.goToPayments')
    },
    {
      key: 'overduePayments',
      icon: 'warning_amber',
      title: this.localization.translate('dashboard.overduePayments.title'),
      value: formatPln(this.overdueAmount()),
      caption: this.localization.translate('dashboard.overduePayments.caption'),
      tooltip: this.localization.translate('dashboard.overduePayments.tooltip'),
      route: '/resources/payments',
      action: this.localization.translate('dashboard.goToPayments')
    }
  ]);

  constructor() {
    void this.loadSummary({ period: 'thisMonth' });
  }

  async applyPeriod(period: DashboardSummaryPeriod): Promise<void> {
    this.selectedPeriod.set(period);
    this.dateFrom.set('');
    this.dateTo.set('');
    await this.loadSummary({ period });
  }

  async applyDateRange(): Promise<void> {
    const dateFrom = this.dateFrom().trim();
    const dateTo = this.dateTo().trim();
    this.selectedPeriod.set(null);
    await this.loadSummary({
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {})
    });
  }

  async clearFilters(): Promise<void> {
    this.selectedPeriod.set(null);
    this.dateFrom.set('');
    this.dateTo.set('');
    this.filteredSummary.set(null);
    await this.loadSummary({});
  }

  onDateFromChange(value: Date | null): void {
    this.dateFrom.set(this.toIsoDate(value));
    this.selectedPeriod.set(null);
  }

  onDateToChange(value: Date | null): void {
    this.dateTo.set(this.toIsoDate(value));
    this.selectedPeriod.set(null);
  }

  private asNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private async loadSummary(filters: DashboardSummaryFilters): Promise<void> {
    const requestId = ++this.summaryRequestId;
    this.loadingSummary.set(true);
    this.filterError.set('');
    try {
      const summary = await firstValueFrom(this.dataService.fetchDashboardSummary$(filters));
      if (requestId !== this.summaryRequestId) {
        return;
      }
      this.filteredSummary.set(summary);
      if (!filters.period && !filters.dateFrom && !filters.dateTo) {
        this.dataService.dashboardSummary.set(summary);
      }
    } catch (error) {
      if (requestId === this.summaryRequestId) {
        this.filterError.set(this.dashboardFilterErrorMessage(error));
      }
    } finally {
      if (requestId === this.summaryRequestId) {
        this.loadingSummary.set(false);
      }
    }
  }

  formatHours(value: unknown): string {
    return new Intl.NumberFormat(this.browserLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(this.asNumber(value));
  }

  categoryLabel(category: string): string {
    return category?.trim() && category !== 'UNKNOWN'
      ? category
      : this.localization.translate('dashboard.lessonCategories.unknown');
  }

  private dashboardFilterErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 422) {
      const payload = error.error && typeof error.error === 'object' ? (error.error as Record<string, unknown>) : {};
      const code = `${payload['code'] ?? payload['errorCode'] ?? payload['error'] ?? ''}`;
      if (code.includes('PERIOD')) {
        return this.localization.translate('dashboard.filter.error.period');
      }
      if (code.includes('DATE_FROM_AFTER_DATE_TO')) {
        return this.localization.translate('dashboard.filter.error.dateOrder');
      }
      return this.localization.translate('dashboard.filter.error.dateFormat');
    }
    return this.localization.translate('dashboard.filter.error.generic');
  }

  private parseIsoDate(value: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return null;
    }
    const [, yearRaw, monthRaw, dayRaw] = match;
    const year = Number(yearRaw);
    const month = Number(monthRaw) - 1;
    const day = Number(dayRaw);
    const date = new Date(year, month, day);
    return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day ? date : null;
  }

  private toIsoDate(value: Date | null): string {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return '';
    }
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0')
    ].join('-');
  }

  private sameId(left: unknown, right: unknown): boolean {
    return `${left ?? ''}`.trim() === `${right ?? ''}`.trim();
  }

  private browserLocale(): string {
    return typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'pl-PL';
  }

  private fallbackSummary(): DashboardSummary {
    const students = this.dataService.students();
    const instructors = this.dataService.instructors();
    const resources = this.dataService.resources();
    const courseInstances = this.dataService.courseInstances();
    const lessons = this.dataService.lessons();
    const payments = this.dataService.payments();
    return {
      students: {
        total: students.length,
        active: students.filter((item) => item.status === 'active').length,
        disabled: students.filter((item) => item.status === 'disabled').length
      },
      instructors: {
        total: instructors.length,
        active: instructors.filter((item) => item.status === 'active').length,
        suspended: instructors.filter((item) => item.status === 'suspended').length,
        disabled: instructors.filter((item) => item.status === 'disabled').length
      },
      resources: {
        total: resources.length,
        available: resources.filter((item) => item.status === 'available' || item.status === 'active').length,
        reserved: resources.filter((item) => item.status === 'reserved').length,
        disabled: resources.filter((item) => item.status === 'disabled').length
      },
      courseInstances: {
        total: courseInstances.length,
        active: courseInstances.filter((item) => item.status === 'active').length,
        finished: courseInstances.filter((item) => item.status === 'finished').length
      },
      lessons: this.fallbackLessonSummary(lessons),
      payments: {
        total: payments.length,
        paidAmount: payments
          .filter((item) => item.status === 'paid' || item.status === 'active')
          .reduce((sum, item) => sum + this.asNumber(item.amount), 0),
        pendingSettlementAmount: payments
          .filter((item) => item.status === 'pendingSettlement')
          .reduce((sum, item) => sum + this.asNumber(item.amount), 0),
        overdueAmount: 0
      }
    };
  }


  private fallbackLessonSummary(lessons: ReturnType<InMemoryDataService['lessons']>): DashboardSummary['lessons'] {
    const summarize = (items: typeof lessons, category?: string): LessonCategorySummary => {
      const hours = (status?: string) => items
        .filter((item) => !status || item.status === status)
        .reduce((sum, item) => sum + this.asNumber(item.durationMinutes) / 60, 0);
      return {
        category: category ?? 'UNKNOWN',
        total: items.length,
        planned: items.filter((item) => item.status === 'planned').length,
        completed: items.filter((item) => item.status === 'completed').length,
        cancelled: items.filter((item) => item.status === 'cancelled').length,
        durationHours: hours(),
        plannedDurationHours: hours('planned'),
        completedDurationHours: hours('completed'),
        cancelledDurationHours: hours('cancelled')
      };
    };
    const categories = new Map<string, typeof lessons>();
    for (const lesson of lessons) {
      const category = lesson.category?.trim() || 'UNKNOWN';
      categories.set(category, [...(categories.get(category) ?? []), lesson]);
    }
    const total = summarize(lessons);
    return {
      ...total,
      byCategory: [...categories.entries()].map(([category, items]) => summarize(items, category))
    };
  }
}
